import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { App, Select } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  fetchSimulations,
  fetchHybridBessWindHistory,
  fetchHybridSimulations,
  createHybridSimulation,
  uploadSimulationGenerationProfile,
  runHybridSimulation,
  fetchHybridSimulationHourlyProfile,
  downloadHybridDailyReport,
  deleteHybridSimulation,
  fetchContainers,
  fetchPCS,
  saveSimulation,
  fetchSimulationDetails,
  fetchSimulationConfig,
  fetchHybridSimulationConfig,
  fetchHybridDischargeProfile,
  saveHybridDischargeProfile,
} from "../../services/bessApi";
import { formatRowsForExcel, applyExcelNumberFormat } from "../../utils/downloadUtils";

// Modular Step Components
import StepProjectInput from "../hybrid-bess/components/StepProjectInput";
import StepHybridInput from "../hybrid-bess/components/StepHybridInput";
import StepDischargeSchedule, { buildDefault24HourMatrix } from "../hybrid-bess/components/StepDischargeSchedule";
import StepUploadProfile from "../hybrid-bess/components/StepUploadProfile";
import StepRunSimulation from "../hybrid-bess/components/StepRunSimulation";
import HybridHistoryTable from "../hybrid-bess/components/HybridHistoryTable";
import HybridBessResultPage from "../hybrid-bess/HybridBessResultPage";

const WIND_STEPS = [
  {
    number: 1,
    title: "Project Input",
    desc: "Define core components, timeframes, targets, and initial system losses.",
  },
  {
    number: 2,
    title: "Hybrid Input",
    desc: "Define loss factors, transformer capacities, and unavailability limits.",
  },
  {
    number: 3,
    title: "Discharge Schedule",
    desc: "Interactive toggle for yearly active battery discharge schedule.",
  },
  {
    number: 4,
    title: "Upload Profile",
    desc: "Upload generation profile spreadsheet and set solar/wind degradation.",
  },
  {
    number: 5,
    title: "Run & Results",
    desc: "Execute the simulation model and analyze chronological outputs.",
  },
];

export default function HybridBessWindPage({ defaultTab }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { message, modal } = App.useApp();

  // If URL contains an ID (e.g. /hybrid-bess-wind/:id), render the full results page with back navigation to wind history
  if (id && id !== "history") {
    return <HybridBessResultPage id={id} backPath="/hybrid-bess-wind/history" />;
  }

  return (
    <HybridBessWindWizard
      navigate={navigate}
      message={message}
      modal={modal}
      defaultTab={defaultTab}
      location={location}
    />
  );
}

function HybridBessWindWizard({ navigate, message, modal, defaultTab, location }) {
  // Navigation & Dropdowns state
  const isHistoryRoute = defaultTab === "history" || (location && location.pathname.endsWith("/history"));
  const [bessSimulations, setBessSimulations] = useState([]);
  const [hybridSimulations, setHybridSimulations] = useState([]);
  const [containerOptions, setContainerOptions] = useState([]);
  const [pcsOptions, setPcsOptions] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(isHistoryRoute ? "history" : "wizard");

  useEffect(() => {
    if (location && location.pathname.endsWith("/history")) {
      setActiveTab("history");
    } else if (location && location.pathname === "/hybrid-bess-wind") {
      setActiveTab("wizard");
    }
  }, [location?.pathname]);

  // Step wizard state: 1, 2, 3, 4
  const [currentStep, setCurrentStep] = useState(1);
  const [createdSimId, setCreatedSimId] = useState(null);
  const [createdBessSizingSimId, setCreatedBessSizingSimId] = useState(null);
  const [bessSizingSimId, setBessSizingSimId] = useState("");
  const [simulationTenure, setSimulationTenure] = useState("");
  // Default simulationType to "2" (Solar + Wind + BESS)
  const [simulationType, setSimulationType] = useState("2");

  const getHybridSimName = (s) => {
    if (s.name) return s.name.replace(/_hybrid$/i, "");
    const baseSim = bessSimulations.find((b) => b.id === parseInt(s.bess_sizing_simulation));
    const baseName =
      s.bess_sizing_simulation_name ||
      baseSim?.name ||
      (s.bess_sizing_simulation ? `Simulation #${s.bess_sizing_simulation}` : `Simulation #${s.id}`);
    return baseName.replace(/_hybrid$/i, "");
  };

  // Step 1: Project Details State
  const [step1Errors, setStep1Errors] = useState({});
  const [step1Touched, setStep1Touched] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const [projectValues, setProjectValues] = useState({
    name: "",
    container_batch: "",
    pcs: "",
    simulation_type: "closed_loop",
    operation_hour: 4,
    container_nos: "",
    pcs_nos: "",
    min_aug_period: "",
    min_aug_container: "",
    max_storage_capacity: "",
    start_year: "",
    simulation_tenure: "",
    energy_poi_mwh: "",
    purchase_years: [],

    // System Losses
    use_220kv_loss: true,
    transmission_line_220kv_loss: "",
    conductor_resistance_220kv_ohm: "",
    transmission_line_220kv_distance_km: "",
    use_transformer_220_loss: true,
    transformer_220kv_loss: "",

    use_400kv_loss: false,
    transmission_line_400kv_loss: "",
    conductor_resistance_400kv_ohm: "",
    transmission_line_400kv_distance_km: "",
    use_transformer_400_loss: false,
    transformer_400kv_loss: "",

    solar_aux_power: "",
    ht_ac_cable_loss: "",
    idt_loss: "",
    lt_ac_cable_loss: "",
    pcs_loss: "",
    lt_dc_cable_loss: "",
    bess_plant_unavailability: "",
    tl_volt_stage_1: 220,
    tl_volt_stage_2: 400,
  });

  // Step 2: Parameters State
  const [dt33kvPct, setDt33kvPct] = useState("");
  const [bessChargingEff, setBessChargingEff] = useState("");
  const [bessDischargingEff, setBessDischargingEff] = useState("");

  const [bessIdtCapacity, setBessIdtCapacity] = useState("");
  const [bessIdtNoLoad, setBessIdtNoLoad] = useState("");
  const [bessIdtLoad, setBessIdtLoad] = useState("");

  const [windIdtCapacity, setWindIdtCapacity] = useState("");
  const [windIdtNoLoad, setWindIdtNoLoad] = useState("");
  const [windIdtLoad, setWindIdtLoad] = useState("");

  const [trans220Capacity, setTrans220Capacity] = useState("");
  const [trans220NoLoad, setTrans220NoLoad] = useState("");
  const [trans220Load, setTrans220Load] = useState("");
  const [transLoss220kv, setTransLoss220kv] = useState("");
  const [useDirectTrans220Loss, setUseDirectTrans220Loss] = useState(false);
  const [has220Transformer, setHas220Transformer] = useState(true);

  const [trans400Capacity, setTrans400Capacity] = useState("");
  const [trans400NoLoad, setTrans400NoLoad] = useState("");
  const [trans400Load, setTrans400Load] = useState("");
  const [transLoss400kv, setTransLoss400kv] = useState("");
  const [useDirectTrans400Loss, setUseDirectTrans400Loss] = useState(false);
  const [has400Transformer, setHas400Transformer] = useState(true);

  const [conductorResistance220, setConductorResistance220] = useState("");
  const [lineDistance220, setLineDistance220] = useState("");
  const [conductorResistance400, setConductorResistance400] = useState("");
  const [lineDistance400, setLineDistance400] = useState("");
  const [has220Line, setHas220Line] = useState(true);
  const [transLineLoss220kv, setTransLineLoss220kv] = useState("");
  const [useDirectLineLoss220, setUseDirectLineLoss220] = useState(false);
  const [transLineLoss400kv, setTransLineLoss400kv] = useState("");
  const [useDirectLineLoss400, setUseDirectLineLoss400] = useState(false);
  const [has400Line, setHas400Line] = useState(true);

  const [bessUnavailability, setBessUnavailability] = useState("");
  const [gridUnavailability, setGridUnavailability] = useState("");

  const [htAcCableLoss, setHtAcCableLoss] = useState("");
  const [idtLoss, setIdtLoss] = useState("");
  const [ltAcCableLoss, setLtAcCableLoss] = useState("");
  const [ltDcCableLoss, setLtDcCableLoss] = useState("");
  const [pcsLoss, setPcsLoss] = useState("");

  const [evacuationCapacity, setEvacuationCapacity] = useState("");
  const [bessFeederCapacity, setBessFeederCapacity] = useState("");
  const [gridFeederCapacity, setGridFeederCapacity] = useState("");
  const [solarFeederCapacity, setSolarFeederCapacity] = useState("");

  const [minBessStoreEnergy, setMinBessStoreEnergy] = useState("");
  const [windTotalLossesPct, setWindTotalLossesPct] = useState("");
  const [minDispatchPower, setMinDispatchPower] = useState("");
  const [bessDegradation, setBessDegradation] = useState(1);

  // Step 3 (Discharge Schedule) State
  const [dischargeProfile, setDischargeProfile] = useState(buildDefault24HourMatrix);
  const [loadingDischarge, setLoadingDischarge] = useState(false);
  const [savingDischarge, setSavingDischarge] = useState(false);

  // Step 4 (Upload Profile) State
  const [uploadFile, setUploadFile] = useState(null);
  const [solarDegradation, setSolarDegradation] = useState("0.4");
  const [uploadDataType, setUploadDataType] = useState("single");
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [profileFilename, setProfileFilename] = useState("");

  // Step 5 (Run & Results) State
  const [runningSim, setRunningSim] = useState(false);
  const [simulationResults, setSimulationResults] = useState(null);
  const [loadedFromHistory, setLoadedFromHistory] = useState(false);
  const [selectedDetailsYear, setSelectedDetailsYear] = useState(null);
  const [hourlyProfileData, setHourlyProfileData] = useState([]);
  const [loadingHourlyData, setLoadingHourlyData] = useState(false);
  const [selectedChartDay, setSelectedChartDay] = useState(1);
  const [downloadingExcelYear, setDownloadingExcelYear] = useState(null);
  const [selectedExportYear, setSelectedExportYear] = useState("all");
  const [selectedTrendFields, setSelectedTrendFields] = useState([
    "solar_gen_33kv_mw",
    "solar_to_bess_power",
    "solar_power_at_poi",
    "grid_import_at_poi",
    "bess_power_at_poi",
  ]);

  // Validation
  const validatePurchaseYears = (values) => {
    if (values.simulation_type !== "closed_loop") return null;
    if (!values.purchase_years || !Array.isArray(values.purchase_years) || values.purchase_years.length === 0) {
      return "At least one purchase year must be added";
    }
    const maxTenure = Number(values.simulation_tenure) || 50;
    for (const item of values.purchase_years) {
      if (typeof item === "object" && item !== null) {
        const yr = Number(item.year);
        const ctrs = Number(item.containers);
        if (item.year === "" || item.year === null || isNaN(yr) || yr < 1 || yr > maxTenure) {
          return `Purchase years must fall within simulation tenure (1 to ${maxTenure})`;
        }
        if (item.containers === "" || item.containers === null || isNaN(ctrs) || ctrs < 1) {
          return "Container count must be at least 1";
        }
      } else {
        const yr = Number(item);
        if (isNaN(yr) || yr < 1 || yr > maxTenure) {
          return `Purchase years must fall within simulation tenure (1 to ${maxTenure})`;
        }
      }
    }
    return null;
  };

  const handlePurchaseYearsChange = (updated) => {
    setProjectValues((prev) => ({ ...prev, purchase_years: updated }));
    if (step1Touched.purchase_years) {
      const err = validatePurchaseYears({ ...projectValues, purchase_years: updated });
      setStep1Errors((prev) => ({ ...prev, purchase_years: err }));
    }
  };

  // Load initial dropdowns
  const loadDropdowns = async () => {
    try {
      setLoadingDropdowns(true);
      const [containers, pcsList, sims, hybridSims] = await Promise.all([
        fetchContainers(),
        fetchPCS(),
        fetchSimulations(),
        fetchHybridBessWindHistory(),
      ]);
      setContainerOptions(containers || []);
      setPcsOptions(pcsList || []);
      setBessSimulations(sims || []);
      setHybridSimulations(hybridSims || []);

      if (containers && containers.length > 0) {
        setProjectValues((prev) => {
          if (prev.container_batch) return prev;
          return { ...prev, container_batch: containers[0].id };
        });
      }
      if (pcsList && pcsList.length > 0) {
        setProjectValues((prev) => {
          if (prev.pcs) return prev;
          return { ...prev, pcs: pcsList[0].id };
        });
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to load equipment options: " + err.message);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const loadHybridSimulations = async () => {
    try {
      setLoadingList(true);
      const data = await fetchHybridBessWindHistory();
      setHybridSimulations(data || []);
    } catch (err) {
      message.error("Failed to load Hybrid BESS Simulations: " + err.message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    loadHybridSimulations();
  }, []);

  // Template Download Handler
  const handleDownloadTemplate = () => {
    const tenure = parseInt(simulationTenure || projectValues.simulation_tenure || 1);
    const yearsToGenerate = uploadDataType === "single" ? 1 : tenure;

    const sampleRow = {
      block: 1,
      ambient_temp_c: 22.8,
      wind_gen_33kv_mw: 8.524,
    };

    for (let i = 1; i <= yearsToGenerate; i++) {
      sampleRow[`year_${i}`] = 10.036118;
    }

    const sampleRows = [sampleRow];
    const worksheet = XLSX.utils.json_to_sheet(sampleRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "data");
    XLSX.writeFile(workbook, "generation_profile_template.xlsx");
  };

  // Step 1: Preview BESS Sizing Results
  const handlePreviewSizing = async () => {
    if (!projectValues.name) {
      message.error("Please enter a Simulation Name before previewing.");
      return;
    }
    if (!projectValues.start_year) {
      message.error("Please enter a Start Year.");
      return;
    }
    if (!projectValues.simulation_tenure) {
      message.error("Please enter Simulation Tenure.");
      return;
    }
    if (projectValues.simulation_type === "closed_loop") {
      const augErr = validatePurchaseYears(projectValues);
      if (augErr) {
        setStep1Errors((prev) => ({ ...prev, purchase_years: augErr }));
        setStep1Touched((prev) => ({ ...prev, purchase_years: true }));
        message.error(augErr);
        return;
      }
    }

    setPreviewLoading(true);
    try {
      const bessPayload = {
        id: createdBessSizingSimId || undefined,
        name: projectValues.name,
        container_batch: projectValues.container_batch || (containerOptions[0]?.id || 1),
        pcs: projectValues.pcs || (pcsOptions[0]?.id || 1),
        simulation_type: projectValues.simulation_type || "closed_loop",
        operation_hour: Number(projectValues.operation_hour) || 4,
        container_nos: projectValues.container_nos !== "" ? Number(projectValues.container_nos) : null,
        pcs_nos: projectValues.pcs_nos !== "" ? Number(projectValues.pcs_nos) : null,
        min_aug_period:
          projectValues.simulation_type === "auto_closed_loop" && projectValues.min_aug_period !== ""
            ? Number(projectValues.min_aug_period)
            : null,
        min_aug_container:
          projectValues.simulation_type === "auto_closed_loop" && projectValues.min_aug_container !== ""
            ? Number(projectValues.min_aug_container)
            : null,
        max_storage_capacity:
          projectValues.simulation_type === "auto_closed_loop" && projectValues.max_storage_capacity !== ""
            ? Number(projectValues.max_storage_capacity)
            : null,
        start_year: Number(projectValues.start_year),
        simulation_tenure: Number(projectValues.simulation_tenure),
        energy_poi_mwh: projectValues.energy_poi_mwh !== "" ? Number(projectValues.energy_poi_mwh) : 0,
        purchase_years: projectValues.purchase_years || [],

        // System Losses
        use_220kv_loss: projectValues.use_220kv_loss,
        transmission_line_220kv_loss:
          projectValues.use_220kv_loss && projectValues.transmission_line_220kv_loss !== ""
            ? Number(projectValues.transmission_line_220kv_loss)
            : null,
        use_transformer_220_loss: projectValues.use_transformer_220_loss,
        transformer_220kv_loss:
          projectValues.use_transformer_220_loss && projectValues.transformer_220kv_loss !== ""
            ? Number(projectValues.transformer_220kv_loss)
            : null,
        use_400kv_loss: projectValues.use_400kv_loss,
        transmission_line_400kv_loss:
          projectValues.use_400kv_loss && projectValues.transmission_line_400kv_loss !== ""
            ? Number(projectValues.transmission_line_400kv_loss)
            : null,
        use_transformer_400_loss: projectValues.use_transformer_400_loss,
        transformer_400kv_loss:
          projectValues.use_transformer_400_loss && projectValues.transformer_400kv_loss !== ""
            ? Number(projectValues.transformer_400kv_loss)
            : null,
        solar_aux_power: projectValues.solar_aux_power !== "" ? Number(projectValues.solar_aux_power) : null,
        ht_ac_cable_loss: projectValues.ht_ac_cable_loss !== "" ? Number(projectValues.ht_ac_cable_loss) : null,
        idt_loss: projectValues.idt_loss !== "" ? Number(projectValues.idt_loss) : null,
        lt_ac_cable_loss: projectValues.lt_ac_cable_loss !== "" ? Number(projectValues.lt_ac_cable_loss) : null,
        pcs_loss: projectValues.pcs_loss !== "" ? Number(projectValues.pcs_loss) : null,
        lt_dc_cable_loss: projectValues.lt_dc_cable_loss !== "" ? Number(projectValues.lt_dc_cable_loss) : null,
        bess_plant_unavailability:
          projectValues.bess_plant_unavailability !== "" ? Number(projectValues.bess_plant_unavailability) : null,
        tl_volt_stage_1: projectValues.tl_volt_stage_1 ? Number(projectValues.tl_volt_stage_1) : 220,
        tl_volt_stage_2: projectValues.tl_volt_stage_2 ? Number(projectValues.tl_volt_stage_2) : 400,
      };

      const savedBessSim = await saveSimulation(bessPayload);
      setCreatedBessSizingSimId(savedBessSim.id);
      setBessSizingSimId(savedBessSim.id.toString());

      const details = await fetchSimulationDetails(savedBessSim.id);
      setPreviewData(details);
      setShowPreview(true);
      message.success("BESS sizing results loaded for preview!");
    } catch (err) {
      console.error(err);
      message.error(err.message || "Failed to generate sizing preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Step 1: Submit Project Details -> Advance to Step 2
  const handleStep1Submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!projectValues.name) {
      message.error("Please enter a Simulation Name.");
      return;
    }
    if (!projectValues.start_year) {
      message.error("Please enter a Start Year.");
      return;
    }
    if (!projectValues.simulation_tenure) {
      message.error("Please enter Simulation Tenure.");
      return;
    }

    if (projectValues.simulation_type === "closed_loop") {
      const augErr = validatePurchaseYears(projectValues);
      if (augErr) {
        setStep1Errors((prev) => ({ ...prev, purchase_years: augErr }));
        setStep1Touched((prev) => ({ ...prev, purchase_years: true }));
        message.error(augErr);
        return;
      }
    }

    setSubmitting(true);
    try {
      const bessPayload = {
        id: createdBessSizingSimId || undefined,
        name: projectValues.name,
        container_batch: projectValues.container_batch || (containerOptions[0]?.id || 1),
        pcs: projectValues.pcs || (pcsOptions[0]?.id || 1),
        simulation_type: projectValues.simulation_type || "closed_loop",
        operation_hour: Number(projectValues.operation_hour) || 4,
        container_nos: projectValues.container_nos !== "" ? Number(projectValues.container_nos) : null,
        pcs_nos: projectValues.pcs_nos !== "" ? Number(projectValues.pcs_nos) : null,
        min_aug_period:
          projectValues.simulation_type === "auto_closed_loop" && projectValues.min_aug_period !== ""
            ? Number(projectValues.min_aug_period)
            : null,
        min_aug_container:
          projectValues.simulation_type === "auto_closed_loop" && projectValues.min_aug_container !== ""
            ? Number(projectValues.min_aug_container)
            : null,
        max_storage_capacity:
          projectValues.simulation_type === "auto_closed_loop" && projectValues.max_storage_capacity !== ""
            ? Number(projectValues.max_storage_capacity)
            : null,
        start_year: Number(projectValues.start_year),
        simulation_tenure: Number(projectValues.simulation_tenure),
        energy_poi_mwh: projectValues.energy_poi_mwh !== "" ? Number(projectValues.energy_poi_mwh) : 0,
        purchase_years: projectValues.purchase_years || [],

        // System Losses
        use_220kv_loss: projectValues.use_220kv_loss,
        transmission_line_220kv_loss:
          projectValues.use_220kv_loss && projectValues.transmission_line_220kv_loss !== ""
            ? Number(projectValues.transmission_line_220kv_loss)
            : null,
        use_transformer_220_loss: projectValues.use_transformer_220_loss,
        transformer_220kv_loss:
          projectValues.use_transformer_220_loss && projectValues.transformer_220kv_loss !== ""
            ? Number(projectValues.transformer_220kv_loss)
            : null,
        use_400kv_loss: projectValues.use_400kv_loss,
        transmission_line_400kv_loss:
          projectValues.use_400kv_loss && projectValues.transmission_line_400kv_loss !== ""
            ? Number(projectValues.transmission_line_400kv_loss)
            : null,
        use_transformer_400_loss: projectValues.use_transformer_400_loss,
        transformer_400kv_loss:
          projectValues.use_transformer_400_loss && projectValues.transformer_400kv_loss !== ""
            ? Number(projectValues.transformer_400kv_loss)
            : null,
        solar_aux_power: projectValues.solar_aux_power !== "" ? Number(projectValues.solar_aux_power) : null,
        ht_ac_cable_loss: projectValues.ht_ac_cable_loss !== "" ? Number(projectValues.ht_ac_cable_loss) : null,
        idt_loss: projectValues.idt_loss !== "" ? Number(projectValues.idt_loss) : null,
        lt_ac_cable_loss: projectValues.lt_ac_cable_loss !== "" ? Number(projectValues.lt_ac_cable_loss) : null,
        pcs_loss: projectValues.pcs_loss !== "" ? Number(projectValues.pcs_loss) : null,
        lt_dc_cable_loss: projectValues.lt_dc_cable_loss !== "" ? Number(projectValues.lt_dc_cable_loss) : null,
        bess_plant_unavailability:
          projectValues.bess_plant_unavailability !== "" ? Number(projectValues.bess_plant_unavailability) : null,
      };

      const savedBessSim = await saveSimulation(bessPayload);
      setCreatedBessSizingSimId(savedBessSim.id);
      setBessSizingSimId(savedBessSim.id.toString());
      setSimulationTenure(projectValues.simulation_tenure.toString());

      // Auto populate Step 2 parameters from Step 1 entries
      setHtAcCableLoss(projectValues.ht_ac_cable_loss !== "" ? projectValues.ht_ac_cable_loss.toString() : "");
      setIdtLoss(projectValues.idt_loss !== "" ? projectValues.idt_loss.toString() : "");
      setLtAcCableLoss(projectValues.lt_ac_cable_loss !== "" ? projectValues.lt_ac_cable_loss.toString() : "");
      setLtDcCableLoss(projectValues.lt_dc_cable_loss !== "" ? projectValues.lt_dc_cable_loss.toString() : "");
      setPcsLoss(projectValues.pcs_loss !== "" ? projectValues.pcs_loss.toString() : "");
      setBessUnavailability(
        projectValues.bess_plant_unavailability !== "" ? projectValues.bess_plant_unavailability.toString() : ""
      );

      setHas220Transformer(projectValues.use_transformer_220_loss || Boolean(projectValues.transformer_220kv_loss));
      setUseDirectTrans220Loss(projectValues.use_transformer_220_loss);
      setTransLoss220kv(
        projectValues.transformer_220kv_loss !== "" ? projectValues.transformer_220kv_loss.toString() : ""
      );

      setHas400Transformer(projectValues.use_transformer_400_loss || Boolean(projectValues.transformer_400kv_loss));
      setUseDirectTrans400Loss(projectValues.use_transformer_400_loss);
      setTransLoss400kv(
        projectValues.transformer_400kv_loss !== "" ? projectValues.transformer_400kv_loss.toString() : ""
      );

      setHas220Line(projectValues.use_220kv_loss || Boolean(projectValues.transmission_line_220kv_loss));
      setUseDirectLineLoss220(projectValues.use_220kv_loss);
      setTransLineLoss220kv(
        projectValues.transmission_line_220kv_loss !== "" ? projectValues.transmission_line_220kv_loss.toString() : ""
      );

      setHas400Line(projectValues.use_400kv_loss || Boolean(projectValues.transmission_line_400kv_loss));
      setUseDirectLineLoss400(projectValues.use_400kv_loss);
      setTransLineLoss400kv(
        projectValues.transmission_line_400kv_loss !== "" ? projectValues.transmission_line_400kv_loss.toString() : ""
      );

      const hybridPayload = {
        bess_sizing_simulation: savedBessSim.id,
        simulation_tenure: parseInt(projectValues.simulation_tenure),
        simulation_type: parseInt(simulationType) || 2,
      };

      const savedHybridSim = await createHybridSimulation(hybridPayload, createdSimId);
      setCreatedSimId(savedHybridSim.id);

      message.success("Project details saved successfully!");
      setCurrentStep(2); // Advance to Step 2 (Hybrid Input)
    } catch (err) {
      console.error(err);
      message.error(err.message || "Failed to save project details");
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: Submit Hybrid Input -> Advance to Step 3 (Upload Profile)
  const handleStep2Submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!createdSimId && !bessSizingSimId) {
      message.error("Simulation session not initialized. Please complete Step 1.");
      return;
    }

    if (
      (simulationType === "2" || simulationType === 2) &&
      (windTotalLossesPct === "" ||
        windTotalLossesPct === null ||
        windTotalLossesPct === undefined ||
        isNaN(parseFloat(windTotalLossesPct)))
    ) {
      message.error("Please enter Wind Total Losses (%).");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        bess_sizing_simulation: parseInt(bessSizingSimId || createdBessSizingSimId || 1),
        simulation_tenure: parseInt(simulationTenure || projectValues.simulation_tenure || 25),
        simulation_type: parseInt(simulationType) || 2,
        tl_volt_stage_1: projectValues.tl_volt_stage_1 ? parseInt(projectValues.tl_volt_stage_1) : 220,
        tl_volt_stage_2: projectValues.tl_volt_stage_2 ? parseInt(projectValues.tl_volt_stage_2) : 400,
        dt_33kv_pct: parseFloat(dt33kvPct || 0),
        bess_charging_efficiency_pct: bessChargingEff ? parseFloat(bessChargingEff) : 94.53,
        bess_discharging_efficiency_pct: bessDischargingEff ? parseFloat(bessDischargingEff) : 95.01,
        bess_idt_installed_capacity_mva: bessIdtCapacity ? parseFloat(bessIdtCapacity) : 0.0,
        bess_idt_noload_losses_mva: bessIdtNoLoad ? parseFloat(bessIdtNoLoad) : 0.0,
        bess_idt_load_losses_mva: bessIdtLoad ? parseFloat(bessIdtLoad) : 0.0,
        wind_idt_installed_capacity_mva:
          (simulationType === "2" || simulationType === 2) && windIdtCapacity ? parseFloat(windIdtCapacity) : 0,
        wind_idt_noload_losses_mva:
          (simulationType === "2" || simulationType === 2) && windIdtNoLoad ? parseFloat(windIdtNoLoad) : 0,
        wind_idt_load_losses_mva:
          (simulationType === "2" || simulationType === 2) && windIdtLoad ? parseFloat(windIdtLoad) : 0,
        transformer_220kv_capacity_mva:
          has220Transformer && !useDirectTrans220Loss && trans220Capacity ? parseFloat(trans220Capacity) : null,
        transformer_220kv_noload_losses_mva:
          has220Transformer && !useDirectTrans220Loss && trans220NoLoad ? parseFloat(trans220NoLoad) : null,
        transformer_220kv_load_losses_mva:
          has220Transformer && !useDirectTrans220Loss && trans220Load ? parseFloat(trans220Load) : null,
        transformer_220kv_loss:
          has220Transformer && useDirectTrans220Loss && transLoss220kv ? parseFloat(transLoss220kv) : null,
        transformer_400kv_capacity_mva:
          has400Transformer && !useDirectTrans400Loss && trans400Capacity ? parseFloat(trans400Capacity) : null,
        transformer_400kv_noload_losses_mva:
          has400Transformer && !useDirectTrans400Loss && trans400NoLoad ? parseFloat(trans400NoLoad) : null,
        transformer_400kv_load_losses_mva:
          has400Transformer && !useDirectTrans400Loss && trans400Load ? parseFloat(trans400Load) : null,
        transformer_400kv_loss:
          has400Transformer && useDirectTrans400Loss && transLoss400kv ? parseFloat(transLoss400kv) : null,
        conductor_resistance_220kv_ohm:
          has220Line && !useDirectLineLoss220 && conductorResistance220 ? parseFloat(conductorResistance220) : null,
        transmission_line_220kv_distance_km:
          has220Line && !useDirectLineLoss220 && lineDistance220 ? parseFloat(lineDistance220) : null,
        conductor_resistance_400kv_ohm:
          has400Line && !useDirectLineLoss400 && conductorResistance400 ? parseFloat(conductorResistance400) : null,
        transmission_line_400kv_distance_km:
          has400Line && !useDirectLineLoss400 && lineDistance400 ? parseFloat(lineDistance400) : null,
        transmission_line_220kv_loss:
          has220Line && useDirectLineLoss220 && transLineLoss220kv ? parseFloat(transLineLoss220kv) : null,
        transmission_line_400kv_loss:
          has400Line && useDirectLineLoss400 && transLineLoss400kv ? parseFloat(transLineLoss400kv) : null,
        bess_unavailability: parseFloat(bessUnavailability || 0),
        grid_unavailability: parseFloat(gridUnavailability || 0),
        ht_ac_cable_loss: htAcCableLoss !== "" ? parseFloat(htAcCableLoss) : null,
        idt_loss: idtLoss !== "" ? parseFloat(idtLoss) : null,
        lt_ac_cable_loss: ltAcCableLoss !== "" ? parseFloat(ltAcCableLoss) : null,
        lt_dc_cable_loss: ltDcCableLoss !== "" ? parseFloat(ltDcCableLoss) : null,
        pcs_loss: pcsLoss !== "" ? parseFloat(pcsLoss) : null,
        evacuation_capacity_mva: parseFloat(evacuationCapacity || 0),
        bess_feeder_capacity_mva: parseFloat(bessFeederCapacity || 0),
        grid_feeder_capacity_mva: gridFeederCapacity ? parseFloat(gridFeederCapacity) : null,
        solar_feeder_capacity_mva: solarFeederCapacity ? parseFloat(solarFeederCapacity) : null,
        min_bess_store_energy: minBessStoreEnergy ? parseFloat(minBessStoreEnergy) : null,
        wind_total_losses:
          (simulationType === "2" || simulationType === 2) && windTotalLossesPct ? parseFloat(windTotalLossesPct) : 0,
        wind_total_losses_pct:
          (simulationType === "2" || simulationType === 2) && windTotalLossesPct ? parseFloat(windTotalLossesPct) : 0,
        min_dispatch_power:
          (simulationType === "2" || simulationType === 2) && minDispatchPower ? parseFloat(minDispatchPower) : 0,
        bess_degradation:
          (simulationType === "2" || simulationType === 2) ? (parseInt(bessDegradation, 10) || 1) : 1,
      };

      const res = await createHybridSimulation(payload, createdSimId);
      message.success("General parameters saved successfully!");
      setCreatedSimId(res.id);
      setCurrentStep(3); // Advance to Step 3 (Discharge Schedule)
      loadHybridSimulations();
    } catch (err) {
      message.error(err.message || "Failed to save parameters");
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch discharge profile as soon as createdSimId is resolved and we enter Step 3
  useEffect(() => {
    if (currentStep === 3 && createdSimId) {
      const loadProfile = async () => {
        try {
          setLoadingDischarge(true);
          const data = await fetchHybridDischargeProfile(createdSimId);
          setDischargeProfile(data && data.length > 0 ? data : buildDefault24HourMatrix());
        } catch (err) {
          message.error("Failed to load discharge profile: " + err.message);
        } finally {
          setLoadingDischarge(false);
        }
      };
      loadProfile();
    }
  }, [currentStep, createdSimId]);

  // Step 3: Save Discharge Profile -> Advance to Step 4 (Upload Profile)
  const handleStep3DischargeSubmit = async () => {
    if (!createdSimId) {
      message.error("Simulation session not initialized. Please complete Step 1 & 2.");
      return;
    }
    try {
      setSavingDischarge(true);
      await saveHybridDischargeProfile(createdSimId, dischargeProfile);
      message.success("Discharge schedule saved successfully!");
      setCurrentStep(4); // Advance to Step 4 (Upload Profile)
    } catch (err) {
      message.error("Failed to save discharge profile: " + err.message);
    } finally {
      setSavingDischarge(false);
    }
  };

  // Step 4: Upload Profile -> Advance to Step 5 (Run Simulation)
  const handleStep4UploadSubmit = async () => {
    if (!uploadFile) {
      if (loadedFromHistory || profileFilename) {
        setCurrentStep(5); // Advance to Step 5
        return;
      }
      setUploadError(true);
      message.error("Please upload the generation profile spreadsheet to proceed.");
      return;
    }
    if (!createdSimId) {
      message.error("Simulation session not initialized. Please go back to Step 1.");
      return;
    }
    if (uploadDataType === "single" && !solarDegradation && solarDegradation !== 0) {
      message.error("Please provide the Degradation Rate for single-year data.");
      return;
    }
    try {
      setUploadingProfile(true);
      setUploadError(false);
      await uploadSimulationGenerationProfile(createdSimId, solarDegradation, uploadFile);
      message.success("Simulation generation profile uploaded successfully!");
      if (uploadFile?.name) {
        setProfileFilename(uploadFile.name);
      }
      setUploadFile(null);
      setCurrentStep(5); // Advance to Step 5 (Run & Results)
    } catch (err) {
      message.error("Upload failed: " + err.message);
    } finally {
      setUploadingProfile(false);
    }
  };

  // Step 4: Run Simulation
  const handleRunSimulation = async () => {
    if (!createdSimId) return;
    try {
      setRunningSim(true);
      await runHybridSimulation(createdSimId);
      message.success("Hybrid simulation run completed successfully!");
      navigate(`/hybrid-bess-wind/${createdSimId}`);
    } catch (err) {
      message.error("Simulation run failed: " + err.message);
    } finally {
      setRunningSim(false);
    }
  };

  const handleLoadHourlyDetails = async (year) => {
    if (!createdSimId) return;
    try {
      setLoadingHourlyData(true);
      setSelectedDetailsYear(year);
      const res = await fetchHybridSimulationHourlyProfile(createdSimId, year);
      setHourlyProfileData(res.hourly_profile || []);
      setSelectedChartDay(1);
    } catch (err) {
      message.error("Failed to load hourly details: " + err.message);
    } finally {
      setLoadingHourlyData(false);
    }
  };

  const handleDownloadYearExcel = async (yearTarget = "all") => {
    const simId = createdSimId;
    if (!simId) {
      message.error("No active simulation found.");
      return;
    }
    setDownloadingExcelYear(yearTarget);
    message.loading({
      content: `Downloading Excel report for ${yearTarget === "all" ? "all years" : `Year ${yearTarget}`}...`,
      key: "download-excel",
    });
    try {
      const dailyData = await downloadHybridDailyReport(simId, yearTarget);
      const dailyProfile = dailyData.hourly_profile || [];
      if (!dailyProfile || dailyProfile.length === 0) {
        message.warning({ content: "No daily profile data available to download.", key: "download-excel" });
        return;
      }

      const wb = XLSX.utils.book_new();
      const yearMap = new Map();
      dailyProfile.forEach((row) => {
        const yr = row.year || 1;
        if (!yearMap.has(yr)) {
          yearMap.set(yr, []);
        }
        yearMap.get(yr).push(row);
      });

      Array.from(yearMap.keys())
        .sort((a, b) => a - b)
        .forEach((yearNum) => {
          const rowsForYear = yearMap.get(yearNum);
          const excelRows = rowsForYear.map((row) => ({
            Year: row.year,
            Month: row.month_num ?? row.month,
            Day: row.day,
            Hour:
              row.hour !== undefined && row.hour !== null
                ? Number(row.hour)
                : row.block
                ? ((row.block - 1) % 24) + 1
                : "",
            "Ambient Temp C": row.ambient_temp_c,
            "Solar Gen 33Kv Mw": row.solar_gen_33kv_mw,
            "Solar Aux Power": row.solar_aux_power,
            "Solar Power At 33Kv": row.solar_power_at_33kv,
            "Solar Power After DT": row.solar_power_after_dt,
            "Battery State": row.battery_state,
            "Total Container No": row.total_container_no,
            "Aux Standby Mw": row.aux_standby_mw ?? row.bess_aux_standby_mw,
            "Aux Operation Mw": row.aux_operation_mw ?? row.bess_aux_operation_mw,
            "Solar Power After Aux": row.solar_power_after_aux ?? row.solar_power_after_bess_aux,
            "Solar Set Point": row.solar_set_point,
            "Curtail Power For Bess Charging": row.solar_to_bess_power ?? row.power_for_bess_charging_at_33kv_bus,
            "Export Solar Power": row.export_solar_power,
            "Bess Capacity": row.bess_capacity,
            "BESS Charge After Losses": row.bess_charge_after_losses,
            "BESS Charge From Solar After Losses": row.bess_solar_after_loss ?? row.bess_charge_from_solar_power,
            "Grid Import For Bess Charging": row.grid_import_for_bess_charging,
            "Grid Import At 33Kv Bus For BESS Charge": row.grid_import_at_33kv_bus,
            "Grid Import At 220Kv Bus For BESS Charge": row.grid_import_at_220kv_bus,
            "Grid Import At 400Kv Bus For BESS Charge": row.grid_import_at_400kv_bus,
            "Grid Import At POI For BESS Charge": row.grid_import_at_poi,
            "Total BESS energy": row.total_bess,
            "Bess Power": row.bess_power,
            "Bess Power After Aux": row.bess_power_after_aux,
            "Bess Power At 33Kv Bus": row.bess_power_at_33kv_bus,
            "Bess Power At 220Kv Bus": row.bess_power_at_220kv_bus,
            "Bess Power At 400Kv Bus": row.bess_power_at_400kv_bus,
            "Bess Power At POI": row.bess_power_at_poi,
            "Bess Power After Unavailability": row.bess_power_after_unavailability,
            "Curtailed BESS Power At 33Kv Bus": row.export_solar_power,
            "Solar Power At 220Kv Bus": row.solar_power_at_220kv_bus,
            "Solar Power At 400Kv Bus": row.solar_power_at_400kv_bus,
            "Solar Power At POI": row.solar_power_at_poi,
            "Solar Power After Unavailability": row.solar_power_after_unavailability,
          }));

          const ws = XLSX.utils.json_to_sheet(formatRowsForExcel(excelRows));
          applyExcelNumberFormat(ws);
          XLSX.utils.book_append_sheet(wb, ws, `Year ${yearNum}`);
        });

      const fileName =
        yearTarget === "all"
          ? `Hybrid_BESS_Wind_Simulation_${simId}_Daily_Outputs.xlsx`
          : `Hybrid_BESS_Wind_Simulation_${simId}_Daily_Year_${yearTarget}.xlsx`;
      XLSX.writeFile(wb, fileName);
      message.success({ content: "Excel report downloaded successfully!", key: "download-excel" });
    } catch (err) {
      console.error(err);
      message.error({ content: "Failed to download Excel report: " + err.message, key: "download-excel" });
    } finally {
      setDownloadingExcelYear(null);
    }
  };

  // History & Dropdown Load Simulation Handler
  const handleLoadSimulation = async (sim) => {
    if (!sim) return;

    let fullSim = sim;
    if (sim.id) {
      try {
        const fetched = await fetchHybridSimulationConfig(sim.id);
        if (fetched) {
          fullSim = { ...sim, ...fetched };
        }
      } catch (e) {
        console.warn("Could not fetch full hybrid config, using existing data:", e);
      }
    }

    setCreatedSimId(fullSim.id);
    const parentBessId = (fullSim.bess_sizing_simulation || "").toString();
    setBessSizingSimId(parentBessId);
    if (fullSim.bess_sizing_simulation) {
      setCreatedBessSizingSimId(fullSim.bess_sizing_simulation);
    }

    // Resolve parent BESS simulation: look up in state or fetch directly if not yet loaded
    let parentBess = bessSimulations.find((s) => Number(s.id) === Number(parentBessId));
    if (!parentBess && parentBessId) {
      try {
        parentBess = await fetchSimulationConfig(parentBessId);
      } catch (e) {
        console.warn("Could not fetch parent BESS config:", e);
      }
    }

    const loadedName = (
      parentBess?.name ||
      fullSim.name ||
      (fullSim.bess_sizing_simulation ? `Simulation #${fullSim.bess_sizing_simulation}` : `Simulation #${fullSim.id}`)
    ).replace(/_hybrid$/i, "");

    // 1. Populate Step 1 (Project Details) state:
    setProjectValues({
      name: loadedName,
      container_batch: fullSim.container_batch || parentBess?.container_batch || (containerOptions[0]?.id || ""),
      pcs: fullSim.pcs || parentBess?.pcs || (pcsOptions[0]?.id || ""),
      simulation_type: parentBess?.simulation_type || fullSim.bess_simulation_type || "closed_loop",
      operation_hour: fullSim.operation_hour ?? parentBess?.operation_hour ?? 4,
      container_nos: fullSim.container_nos ?? parentBess?.container_nos ?? "",
      pcs_nos: fullSim.pcs_nos ?? parentBess?.pcs_nos ?? "",
      min_aug_period: fullSim.min_aug_period ?? parentBess?.min_aug_period ?? "",
      min_aug_container: fullSim.min_aug_container ?? parentBess?.min_aug_container ?? "",
      max_storage_capacity: fullSim.max_storage_capacity ?? parentBess?.max_storage_capacity ?? "",
      start_year: fullSim.start_year ?? parentBess?.start_year ?? "",
      simulation_tenure: fullSim.simulation_tenure ?? parentBess?.simulation_tenure ?? "",
      energy_poi_mwh: fullSim.energy_poi_mwh ?? parentBess?.energy_poi_mwh ?? "",
      purchase_years: fullSim.purchase_years || parentBess?.purchase_years || [],

      // System Losses
      use_220kv_loss:
        fullSim.use_220kv_loss ??
        parentBess?.use_220kv_loss ??
        (fullSim.transmission_line_220kv_loss != null && fullSim.transmission_line_220kv_loss !== ""),
      transmission_line_220kv_loss:
        fullSim.transmission_line_220kv_loss ?? parentBess?.transmission_line_220kv_loss ?? "",
      conductor_resistance_220kv_ohm:
        fullSim.conductor_resistance_220kv_ohm ?? parentBess?.conductor_resistance_220kv_ohm ?? "",
      transmission_line_220kv_distance_km:
        fullSim.transmission_line_220kv_distance_km ?? parentBess?.transmission_line_220kv_distance_km ?? "",

      use_transformer_220_loss:
        fullSim.use_transformer_220_loss ??
        parentBess?.use_transformer_220_loss ??
        (fullSim.transformer_220kv_loss != null && fullSim.transformer_220kv_loss !== ""),
      transformer_220kv_loss: fullSim.transformer_220kv_loss ?? parentBess?.transformer_220kv_loss ?? "",

      use_400kv_loss:
        fullSim.use_400kv_loss ??
        parentBess?.use_400kv_loss ??
        (fullSim.transmission_line_400kv_loss != null && fullSim.transmission_line_400kv_loss !== ""),
      transmission_line_400kv_loss:
        fullSim.transmission_line_400kv_loss ?? parentBess?.transmission_line_400kv_loss ?? "",
      conductor_resistance_400kv_ohm:
        fullSim.conductor_resistance_400kv_ohm ?? parentBess?.conductor_resistance_400kv_ohm ?? "",
      transmission_line_400kv_distance_km:
        fullSim.transmission_line_400kv_distance_km ?? parentBess?.transmission_line_400kv_distance_km ?? "",

      use_transformer_400_loss:
        fullSim.use_transformer_400_loss ??
        parentBess?.use_transformer_400_loss ??
        (fullSim.transformer_400kv_loss != null && fullSim.transformer_400kv_loss !== ""),
      transformer_400kv_loss: fullSim.transformer_400kv_loss ?? parentBess?.transformer_400kv_loss ?? "",

      solar_aux_power: fullSim.solar_aux_power ?? parentBess?.solar_aux_power ?? "",
      ht_ac_cable_loss: fullSim.ht_ac_cable_loss ?? parentBess?.ht_ac_cable_loss ?? "",
      idt_loss: fullSim.idt_loss ?? parentBess?.idt_loss ?? "",
      lt_ac_cable_loss: fullSim.lt_ac_cable_loss ?? parentBess?.lt_ac_cable_loss ?? "",
      pcs_loss: fullSim.pcs_loss ?? parentBess?.pcs_loss ?? "",
      lt_dc_cable_loss: fullSim.lt_dc_cable_loss ?? parentBess?.lt_dc_cable_loss ?? "",
      bess_plant_unavailability:
        fullSim.bess_plant_unavailability ??
        fullSim.bess_unavailability ??
        parentBess?.bess_plant_unavailability ??
        parentBess?.bess_unavailability ??
        "",
      tl_volt_stage_1: fullSim.tl_volt_stage_1 ?? parentBess?.tl_volt_stage_1 ?? 220,
      tl_volt_stage_2: fullSim.tl_volt_stage_2 ?? parentBess?.tl_volt_stage_2 ?? 400,
    });

    // 2. Populate Step 2 (Hybrid Parameters) discrete states:
    // 220kV Transformer
    if (fullSim.transformer_220kv_loss != null && fullSim.transformer_220kv_loss !== "") {
      setHas220Transformer(true);
      setUseDirectTrans220Loss(true);
      setTransLoss220kv(fullSim.transformer_220kv_loss.toString());
    } else if (fullSim.transformer_220kv_capacity_mva != null && parseFloat(fullSim.transformer_220kv_capacity_mva) > 0) {
      setHas220Transformer(true);
      setUseDirectTrans220Loss(false);
      setTransLoss220kv("");
    } else if (parentBess && (parentBess.transformer_220kv_loss != null && parentBess.transformer_220kv_loss !== "")) {
      setHas220Transformer(true);
      setUseDirectTrans220Loss(true);
      setTransLoss220kv(parentBess.transformer_220kv_loss.toString());
    } else {
      setHas220Transformer(false);
      setUseDirectTrans220Loss(false);
      setTransLoss220kv("");
    }

    // 400kV Transformer
    if (fullSim.transformer_400kv_loss != null && fullSim.transformer_400kv_loss !== "") {
      setHas400Transformer(true);
      setUseDirectTrans400Loss(true);
      setTransLoss400kv(fullSim.transformer_400kv_loss.toString());
    } else if (fullSim.transformer_400kv_capacity_mva != null && parseFloat(fullSim.transformer_400kv_capacity_mva) > 0) {
      setHas400Transformer(true);
      setUseDirectTrans400Loss(false);
      setTransLoss400kv("");
    } else if (parentBess && (parentBess.transformer_400kv_loss != null && parentBess.transformer_400kv_loss !== "")) {
      setHas400Transformer(true);
      setUseDirectTrans400Loss(true);
      setTransLoss400kv(parentBess.transformer_400kv_loss.toString());
    } else {
      setHas400Transformer(false);
      setUseDirectTrans400Loss(false);
      setTransLoss400kv("");
    }

    // 220kV Transmission Line
    if (fullSim.transmission_line_220kv_loss != null && fullSim.transmission_line_220kv_loss !== "") {
      setHas220Line(true);
      setUseDirectLineLoss220(true);
      setTransLineLoss220kv(fullSim.transmission_line_220kv_loss.toString());
      setConductorResistance220("");
      setLineDistance220("");
    } else if (
      (fullSim.conductor_resistance_220kv_ohm != null && fullSim.conductor_resistance_220kv_ohm !== "") ||
      (fullSim.transmission_line_220kv_distance_km != null && fullSim.transmission_line_220kv_distance_km !== "")
    ) {
      setHas220Line(true);
      setUseDirectLineLoss220(false);
      setTransLineLoss220kv("");
      setConductorResistance220((fullSim.conductor_resistance_220kv_ohm || "").toString());
      setLineDistance220((fullSim.transmission_line_220kv_distance_km || "").toString());
    } else if (parentBess && (parentBess.transmission_line_220kv_loss != null && parentBess.transmission_line_220kv_loss !== "")) {
      setHas220Line(true);
      setUseDirectLineLoss220(true);
      setTransLineLoss220kv(parentBess.transmission_line_220kv_loss.toString());
      setConductorResistance220("");
      setLineDistance220("");
    } else if (fullSim.conductor_resistance_ohm != null || fullSim.transmission_line_distance_km != null) {
      setHas220Line(true);
      setUseDirectLineLoss220(false);
      setTransLineLoss220kv("");
      setConductorResistance220((fullSim.conductor_resistance_ohm || "").toString());
      setLineDistance220((fullSim.transmission_line_distance_km || "").toString());
    } else {
      setHas220Line(false);
      setUseDirectLineLoss220(false);
      setTransLineLoss220kv("");
      setConductorResistance220("");
      setLineDistance220("");
    }

    // 400kV Transmission Line
    if (fullSim.transmission_line_400kv_loss != null && fullSim.transmission_line_400kv_loss !== "") {
      setHas400Line(true);
      setUseDirectLineLoss400(true);
      setTransLineLoss400kv(fullSim.transmission_line_400kv_loss.toString());
      setConductorResistance400("");
      setLineDistance400("");
    } else if (
      (fullSim.conductor_resistance_400kv_ohm != null && fullSim.conductor_resistance_400kv_ohm !== "") ||
      (fullSim.transmission_line_400kv_distance_km != null && fullSim.transmission_line_400kv_distance_km !== "")
    ) {
      setHas400Line(true);
      setUseDirectLineLoss400(false);
      setTransLineLoss400kv("");
      setConductorResistance400((fullSim.conductor_resistance_400kv_ohm || "").toString());
      setLineDistance400((fullSim.transmission_line_400kv_distance_km || "").toString());
    } else if (parentBess && (parentBess.transmission_line_400kv_loss != null && parentBess.transmission_line_400kv_loss !== "")) {
      setHas400Line(true);
      setUseDirectLineLoss400(true);
      setTransLineLoss400kv(parentBess.transmission_line_400kv_loss.toString());
      setConductorResistance400("");
      setLineDistance400("");
    } else {
      setHas400Line(false);
      setUseDirectLineLoss400(false);
      setTransLineLoss400kv("");
      setConductorResistance400("");
      setLineDistance400("");
    }

    // Cable & Equipment Losses
    setHtAcCableLoss((fullSim.ht_ac_cable_loss ?? parentBess?.ht_ac_cable_loss ?? "").toString());
    setIdtLoss((fullSim.idt_loss ?? parentBess?.idt_loss ?? "").toString());
    setLtAcCableLoss((fullSim.lt_ac_cable_loss ?? parentBess?.lt_ac_cable_loss ?? "").toString());
    setLtDcCableLoss((fullSim.lt_dc_cable_loss ?? parentBess?.lt_dc_cable_loss ?? "").toString());
    setPcsLoss((fullSim.pcs_loss ?? parentBess?.pcs_loss ?? "").toString());

    // Plant Unavailability
    const loadUnavail =
      fullSim.bess_unavailability ??
      fullSim.bess_plant_unavailability ??
      parentBess?.bess_plant_unavailability ??
      parentBess?.bess_unavailability ??
      "";
    setBessUnavailability(loadUnavail !== "" ? parseFloat(loadUnavail).toString() : "");
    setGridUnavailability((fullSim.grid_unavailability ?? parentBess?.grid_unavailability ?? "").toString());

    // Wind losses & Dispatch power
    setWindTotalLossesPct((fullSim.wind_total_losses ?? fullSim.wind_total_losses_pct ?? "").toString());
    setMinDispatchPower((fullSim.min_dispatch_power ?? "").toString());
    setBessDegradation(fullSim.bess_degradation != null ? Number(fullSim.bess_degradation) : 1);

    // Feeders & Capacities
    setEvacuationCapacity((fullSim.evacuation_capacity_mva || "").toString());
    setBessFeederCapacity((fullSim.bess_feeder_capacity_mva || "").toString());
    setGridFeederCapacity((fullSim.grid_feeder_capacity_mva || "").toString());
    setSolarFeederCapacity((fullSim.solar_feeder_capacity_mva || "").toString());
    setMinBessStoreEnergy((fullSim.min_bess_store_energy || "").toString());

    // IDTs & Transformer capacities
    setDt33kvPct((fullSim.dt_33kv_pct || "").toString());
    setBessChargingEff((fullSim.bess_charging_efficiency_pct || "").toString());
    setBessDischargingEff((fullSim.bess_discharging_efficiency_pct || "").toString());
    setBessIdtCapacity((fullSim.bess_idt_installed_capacity_mva || "").toString());
    setBessIdtNoLoad((fullSim.bess_idt_noload_losses_mva || "").toString());
    setBessIdtLoad((fullSim.bess_idt_load_losses_mva || "").toString());
    setWindIdtCapacity((fullSim.wind_idt_installed_capacity_mva || "").toString());
    setWindIdtNoLoad((fullSim.wind_idt_noload_losses_mva || "").toString());
    setWindIdtLoad((fullSim.wind_idt_load_losses_mva || "").toString());
    setTrans220Capacity((fullSim.transformer_220kv_capacity_mva || "").toString());
    setTrans220NoLoad((fullSim.transformer_220kv_noload_losses_mva || "").toString());
    setTrans220Load((fullSim.transformer_220kv_load_losses_mva || "").toString());
    setTrans400Capacity((fullSim.transformer_400kv_capacity_mva || "").toString());
    setTrans400NoLoad((fullSim.transformer_400kv_noload_losses_mva || "").toString());
    setTrans400Load((fullSim.transformer_400kv_load_losses_mva || "").toString());

    // Profile & Meta
    setProfileFilename(fullSim.profile_filename || "");
    setSimulationTenure((fullSim.simulation_tenure || "").toString());
    setSimulationType((fullSim.simulation_type || "2").toString());

    try {
      const dischargeData = await fetchHybridDischargeProfile(fullSim.id);
      setDischargeProfile(dischargeData && dischargeData.length > 0 ? dischargeData : buildDefault24HourMatrix());
    } catch (e) {
      console.warn("Could not load discharge profile:", e);
      setDischargeProfile(buildDefault24HourMatrix());
    }

    setCurrentStep(1);
    setActiveTab("wizard");
    if (navigate) navigate("/hybrid-bess-wind");
    setSimulationResults(null);
    setLoadedFromHistory(true);
  };

  const handleDeleteHybridSimulation = (idToDelete) => {
    modal.confirm({
      title: "Are you sure you want to delete this hybrid simulation?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          message.loading({ content: "Deleting simulation...", key: "delete-hybrid-sim" });
          await deleteHybridSimulation(idToDelete);
          message.success({ content: "Simulation deleted successfully!", key: "delete-hybrid-sim" });
          setHybridSimulations((prev) => prev.filter((s) => s.id !== idToDelete));
        } catch (err) {
          console.error(err);
          message.error({ content: "Failed to delete simulation: " + err.message, key: "delete-hybrid-sim" });
        }
      },
    });
  };

  const startFreshSimulation = () => {
    setCreatedSimId(null);
    setBessSizingSimId("");
    setSimulationTenure("");
    setSimulationType("2");
    setDt33kvPct("");
    setBessChargingEff("");
    setBessDischargingEff("");
    setBessIdtCapacity("");
    setBessIdtNoLoad("");
    setBessIdtLoad("");
    setWindIdtCapacity("");
    setWindIdtNoLoad("");
    setWindIdtLoad("");
    setTrans220Capacity("");
    setTrans220NoLoad("");
    setTrans220Load("");
    setTransLoss220kv("");
    setUseDirectTrans220Loss(false);
    setHas220Transformer(true);
    setTrans400Capacity("");
    setTrans400NoLoad("");
    setTrans400Load("");
    setTransLoss400kv("");
    setUseDirectTrans400Loss(false);
    setHas400Transformer(true);
    setConductorResistance220("");
    setLineDistance220("");
    setConductorResistance400("");
    setLineDistance400("");
    setTransLineLoss220kv("");
    setUseDirectLineLoss220(false);
    setHas220Line(true);
    setTransLineLoss400kv("");
    setUseDirectLineLoss400(false);
    setHas400Line(true);
    setBessUnavailability("");
    setGridUnavailability("");
    setEvacuationCapacity("");
    setBessFeederCapacity("");
    setGridFeederCapacity("");
    setSolarFeederCapacity("");
    setMinBessStoreEnergy("");
    setWindTotalLossesPct("");
    setMinDispatchPower("");
    setBessDegradation(1);
    setDischargeProfile(buildDefault24HourMatrix());
    setSimulationResults(null);
    setLoadedFromHistory(false);
    setProfileFilename("");
    setCurrentStep(1);
    setProjectValues({
      name: "",
      container_batch: "",
      pcs: "",
      simulation_type: "closed_loop",
      operation_hour: 4,
      container_nos: "",
      pcs_nos: "",
      min_aug_period: "",
      min_aug_container: "",
      max_storage_capacity: "",
      start_year: "",
      simulation_tenure: "",
      energy_poi_mwh: "",
      purchase_years: [],
      use_220kv_loss: true,
      transmission_line_220kv_loss: "",
      conductor_resistance_220kv_ohm: "",
      transmission_line_220kv_distance_km: "",
      use_transformer_220_loss: true,
      transformer_220kv_loss: "",
      use_400kv_loss: false,
      transmission_line_400kv_loss: "",
      conductor_resistance_400kv_ohm: "",
      transmission_line_400kv_distance_km: "",
      use_transformer_400_loss: false,
      transformer_400kv_loss: "",
      solar_aux_power: "",
      ht_ac_cable_loss: "",
      idt_loss: "",
      lt_ac_cable_loss: "",
      pcs_loss: "",
      lt_dc_cable_loss: "",
      bess_plant_unavailability: "",
      tl_volt_stage_1: 220,
      tl_volt_stage_2: 400,
    });
  };

  const handleStartNewSimulation = () => {
    const modalInstance = modal.info({
      title: "Start New Simulation",
      icon: <InfoCircleOutlined />,
      footer: null,
      closable: true,
      content: (
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            Would you like to copy the current configuration for the new simulation or start fresh?
          </p>
          <div className="flex gap-3 justify-end mt-2">
            <button
              className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
              onClick={() => {
                startFreshSimulation();
                message.success("Started a fresh simulation.");
                modalInstance.destroy();
              }}
            >
              Start Fresh
            </button>
            <button
              className="px-4 py-2 bg-[#0052cc] text-white hover:bg-[#0043a4] rounded-lg text-sm font-semibold transition-colors cursor-pointer"
              onClick={() => {
                setCreatedSimId(null);
                setProjectValues((prev) => ({
                  ...prev,
                  name: prev.name ? prev.name + " (Copy)" : "New Wind Simulation",
                }));
                setLoadedFromHistory(false);
                message.success("Configuration copied. You can now save it as a new simulation.");
                modalInstance.destroy();
              }}
            >
              Copy Configuration
            </button>
          </div>
        </div>
      ),
    });
  };

  return (
    <div className="bess-container-redesign min-h-screen pt-2 px-6 pb-6 md:pt-4 md:px-8 md:pb-8 flex flex-col">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hanken+Grotesk:ital,wght@0,100..900;1,100..900&family=Geist:wght@100..900&family=Inter:ital,wght@0,100..900;1,100..900&display=swap');

        .bess-container-redesign {
          background-color: #f7f9fb !important;
          font-family: 'Inter', sans-serif !important;
          color: #191c1e !important;
        }

        .bess-container-redesign h1,
        .bess-container-redesign h2,
        .bess-container-redesign h3,
        .bess-container-redesign h4 {
          font-family: 'Hanken Grotesk', sans-serif !important;
          font-weight: 600 !important;
          border-left: none !important;
          padding-left: 0 !important;
          text-transform: none !important;
          margin: 0 !important;
          letter-spacing: -0.01em !important;
        }

        .bess-container-redesign h4.form-section-title {
          font-size: 16px !important;
          font-weight: 700 !important;
        }

        .bess-container-redesign .headline-lg {
          font-size: 30px !important;
          line-height: 38px !important;
          letter-spacing: -0.02em !important;
        }

        .bess-layout-content .bess-container-redesign label {
          color: #0c2340 !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          margin-bottom: 8px !important;
          display: block !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]) {
          height: 44px !important;
          padding: 0 14px !important;
          font-size: 14px !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 400 !important;
          color: #191c1e !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          background-color: #ffffff !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: none !important;
          box-sizing: border-box !important;
          width: 100% !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):hover {
          border-color: #707881 !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):focus {
          border-color: #0052CC !important;
          box-shadow: 0 0 0 3px #dbeafe !important;
          outline: none !important;
        }

        .bess-card {
          background-color: #ffffff !important;
          border-radius: 10px !important;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.02) !important;
          padding: 32px !important;
          border: 1px solid #eceef0 !important;
          box-sizing: border-box !important;
        }

        .bess-container-redesign .bess-antd-select.ant-select {
          height: 44px !important;
        }

        .bess-container-redesign .bess-antd-select.ant-select .ant-select-selector {
          background-color: #ffffff !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          height: 44px !important;
          display: flex !important;
          align-items: center !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: none !important;
        }

        .bess-container-redesign .bess-antd-select.ant-select .ant-select-arrow {
          top: 50% !important;
          margin-top: 0 !important;
          transform: translateY(-50%) !important;
        }

        .bess-container-redesign .bess-antd-select.ant-select:hover .ant-select-selector {
          border-color: #707881 !important;
        }

        .bess-container-redesign .bess-antd-select.ant-select-focused .ant-select-selector {
          border-color: #0052CC !important;
          box-shadow: 0 0 0 3px #dbeafe !important;
        }

        .bess-stepper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          margin: 0 auto 28px auto;
          user-select: none;
          padding: 0 2px;
        }

        .bess-step-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        .bess-step-circle {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
          transition: all 0.3s ease;
          flex-shrink: 0;
          text-align: center;
        }

        .bess-step-circle.active {
          background-color: #0052CC !important;
          color: #ffffff !important;
        }

        .bess-step-circle.completed {
          background-color: #e0f2fe !important;
          color: #0052CC !important;
        }

        .bess-step-circle.inactive {
          background-color: #d8dadc !important;
          color: #707881 !important;
        }

        .bess-step-label {
          font-size: 12px;
          font-weight: 600;
          line-height: 1;
          white-space: nowrap;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
        }

        .bess-step-label.active {
          color: #0052CC !important;
        }

        .bess-step-label.inactive {
          color: #707881 !important;
        }

        .bess-step-line {
          flex: 1 1 0%;
          height: 2px;
          background-color: #d8dadc;
          margin: 0 10px;
          min-width: 16px;
          align-self: center;
          transition: all 0.3s ease;
        }

        .bess-step-line.active {
          background-color: #0052CC;
        }

        .bess-btn-primary {
          background-color: #0052CC !important;
          color: #ffffff !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          border: none !important;
          border-radius: 8px !important;
          height: 44px !important;
          padding: 0 24px !important;
          cursor: pointer !important;
          transition: background-color 0.2s ease-in-out !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        .bess-btn-primary:hover {
          background-color: #0073e6 !important;
        }

        .bess-btn-secondary {
          background-color: #ffffff !important;
          color: #707881 !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 500 !important;
          font-size: 14px !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          height: 44px !important;
          padding: 0 20px !important;
          cursor: pointer !important;
          transition: all 0.2s ease-in-out !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        .bess-btn-secondary:hover {
          border-color: #707881 !important;
          color: #191c1e !important;
        }

        .bess-table {
          width: 100% !important;
          border-collapse: collapse !important;
        }

        .bess-table th {
          background-color: #f8fafc !important;
          font-family: 'Geist', sans-serif !important;
          font-size: 11px !important;
          font-weight: 500 !important;
          color: #707881 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          padding: 10px 16px !important;
          border-bottom: 1px solid #e6e8ea !important;
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
        }

        .bess-table td {
          padding: 12px 16px !important;
          border-bottom: 1px solid #f2f4f6 !important;
          font-size: 13.5px !important;
          color: #191c1e !important;
        }

        input::placeholder {
          color: #94a3b8 !important;
          opacity: 0.55 !important;
        }
      `}</style>

      <div className="max-w-[1280px] mx-auto w-full flex-1">
        {/* Header Title Block with Inline Tabs */}
        <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-[#bfc7d2] pb-4 gap-4 mb-6">
          <div>
            <h1 className="headline-lg font-bold text-[#0c2340]">Solar + Wind + Bess Simulation</h1>
            <p className="text-xs text-slate-500 mt-1">
              Solar and wind generation profile and battery storage sizing simulation module
            </p>
          </div>
          <div className="flex items-center gap-3">
            {loadedFromHistory && (
              <button
                type="button"
                onClick={handleStartNewSimulation}
                className="h-10 px-5 text-xs font-bold rounded-lg transition-all cursor-pointer bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100/50 shadow-sm flex items-center justify-center gap-1.5"
                title="Start a new simulation from scratch or copy the current one"
              >
                Start New Simulation
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setActiveTab("wizard");
                navigate("/hybrid-bess-wind");
              }}
              className={`h-10 px-5 text-xs font-bold rounded-lg transition-all cursor-pointer bg-white shadow-sm ${
                activeTab === "wizard"
                  ? "border border-blue-500 text-blue-600"
                  : "border border-slate-200 text-slate-600 hover:text-slate-800"
              }`}
            >
              Simulation
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("history");
                navigate("/hybrid-bess-wind/history");
              }}
              className={`h-10 px-5 text-xs font-bold rounded-lg transition-all cursor-pointer bg-white shadow-sm ${
                activeTab === "history"
                  ? "border border-blue-500 text-blue-600"
                  : "border border-slate-200 text-slate-600 hover:text-slate-800"
              }`}
            >
              History ({hybridSimulations.length})
            </button>
          </div>
        </div>

        {activeTab === "wizard" ? (
          <div className="space-y-6">
            {/* 4-Step Stepper Progress Indicator */}
            <div className="bess-stepper">
              {WIND_STEPS.map((s, index, stepsList) => (
                <React.Fragment key={s.number}>
                  <div className="bess-step-item">
                    <div
                      className={`bess-step-circle ${
                        currentStep === s.number
                          ? "active"
                          : currentStep > s.number
                          ? "completed"
                          : "inactive"
                      }`}
                    >
                      {currentStep > s.number ? "✓" : s.number}
                    </div>
                    <span
                      className={`bess-step-label ${
                        currentStep === s.number ? "active" : "inactive"
                      }`}
                    >
                      {s.title}
                    </span>
                  </div>
                  {index < stepsList.length - 1 && (
                    <div className={`bess-step-line ${currentStep > s.number ? "active" : ""}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Active Step Form Card */}
            <div className="bess-card animate-fade-in">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
                <div>
                  <span className="text-xs font-bold text-[#0052CC] tracking-wide block mb-1">
                    Step {currentStep} of 5
                  </span>
                  <h2 className="text-2xl font-bold text-slate-800 mb-1">
                    {WIND_STEPS[currentStep - 1]?.title}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {WIND_STEPS[currentStep - 1]?.desc}
                  </p>
                </div>
                {currentStep === 1 && (
                  <div className="flex items-center gap-2 pt-1">
                    <Select
                      placeholder="Select Past Hybrid Simulation..."
                      value={createdSimId ? Number(createdSimId) : undefined}
                      onChange={async (simId) => {
                        if (!simId) {
                          startFreshSimulation();
                          return;
                        }
                        const matched = hybridSimulations.find((s) => Number(s.id) === Number(simId));
                        if (matched) {
                          await handleLoadSimulation(matched);
                        } else {
                          await handleLoadSimulation({ id: simId });
                        }
                      }}
                      allowClear
                      onClear={() => startFreshSimulation()}
                      style={{ minWidth: 280 }}
                      className="bess-antd-select"
                      loading={loadingDropdowns || loadingList}
                      options={[...hybridSimulations]
                        .sort((a, b) => {
                          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
                          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
                          if (timeB !== timeA) return timeB - timeA;
                          return (Number(b.id) || 0) - (Number(a.id) || 0);
                        })
                        .map((s) => ({
                          value: Number(s.id),
                          label: `${getHybridSimName(s)} (${s.simulation_tenure || 25} Yrs)`
                        }))}
                    />
                  </div>
                )}
              </div>

              {/* Step 1: Project Details Form */}
              {currentStep === 1 && (
                <StepProjectInput
                  projectValues={projectValues}
                  setProjectValues={setProjectValues}
                  containerOptions={containerOptions}
                  pcsOptions={pcsOptions}
                  loadingDropdowns={loadingDropdowns}
                  step1Errors={step1Errors}
                  setStep1Errors={setStep1Errors}
                  step1Touched={step1Touched}
                  setStep1Touched={setStep1Touched}
                  validatePurchaseYears={validatePurchaseYears}
                  handlePurchaseYearsChange={handlePurchaseYearsChange}
                  previewLoading={previewLoading}
                  previewData={previewData}
                  showPreview={showPreview}
                  setShowPreview={setShowPreview}
                  handlePreviewSizing={handlePreviewSizing}
                  onSubmit={handleStep1Submit}
                  submitting={submitting}
                />
              )}

              {/* Step 2: Hybrid Parameters Form */}
              {currentStep === 2 && (
                <StepHybridInput
                  projectValues={projectValues}
                  setProjectValues={setProjectValues}
                  simulationType={simulationType}
                  setSimulationType={setSimulationType}
                  has220Line={has220Line}
                  conductorResistance220={conductorResistance220}
                  setConductorResistance220={setConductorResistance220}
                  lineDistance220={lineDistance220}
                  setLineDistance220={setLineDistance220}
                  useDirectLineLoss220={useDirectLineLoss220}
                  setUseDirectLineLoss220={setUseDirectLineLoss220}
                  transLineLoss220kv={transLineLoss220kv}
                  setTransLineLoss220kv={setTransLineLoss220kv}
                  has400Line={has400Line}
                  conductorResistance400={conductorResistance400}
                  setConductorResistance400={setConductorResistance400}
                  lineDistance400={lineDistance400}
                  setLineDistance400={setLineDistance400}
                  useDirectLineLoss400={useDirectLineLoss400}
                  setUseDirectLineLoss400={setUseDirectLineLoss400}
                  transLineLoss400kv={transLineLoss400kv}
                  setTransLineLoss400kv={setTransLineLoss400kv}
                  htAcCableLoss={htAcCableLoss}
                  ltAcCableLoss={ltAcCableLoss}
                  ltDcCableLoss={ltDcCableLoss}
                  pcsLoss={pcsLoss}
                  idtLoss={idtLoss}
                  bessIdtNoLoad={bessIdtNoLoad}
                  setBessIdtNoLoad={setBessIdtNoLoad}
                  windIdtCapacity={windIdtCapacity}
                  setWindIdtCapacity={setWindIdtCapacity}
                  windIdtNoLoad={windIdtNoLoad}
                  setWindIdtNoLoad={setWindIdtNoLoad}
                  windIdtLoad={windIdtLoad}
                  setWindIdtLoad={setWindIdtLoad}
                  has220Transformer={has220Transformer}
                  trans220Capacity={trans220Capacity}
                  setTrans220Capacity={setTrans220Capacity}
                  trans220NoLoad={trans220NoLoad}
                  setTrans220NoLoad={setTrans220NoLoad}
                  trans220Load={trans220Load}
                  setTrans220Load={setTrans220Load}
                  useDirectTrans220Loss={useDirectTrans220Loss}
                  setUseDirectTrans220Loss={setUseDirectTrans220Loss}
                  transLoss220kv={transLoss220kv}
                  setTransLoss220kv={setTransLoss220kv}
                  has400Transformer={has400Transformer}
                  trans400Capacity={trans400Capacity}
                  setTrans400Capacity={setTrans400Capacity}
                  trans400NoLoad={trans400NoLoad}
                  setTrans400NoLoad={setTrans400NoLoad}
                  trans400Load={trans400Load}
                  setTrans400Load={setTrans400Load}
                  useDirectTrans400Loss={useDirectTrans400Loss}
                  setUseDirectTrans400Loss={setUseDirectTrans400Loss}
                  transLoss400kv={transLoss400kv}
                  setTransLoss400kv={setTransLoss400kv}
                  dt33kvPct={dt33kvPct}
                  setDt33kvPct={setDt33kvPct}
                  bessUnavailability={bessUnavailability}
                  setBessUnavailability={setBessUnavailability}
                  gridUnavailability={gridUnavailability}
                  setGridUnavailability={setGridUnavailability}
                  evacuationCapacity={evacuationCapacity}
                  setEvacuationCapacity={setEvacuationCapacity}
                  bessFeederCapacity={bessFeederCapacity}
                  setBessFeederCapacity={setBessFeederCapacity}
                  minBessStoreEnergy={minBessStoreEnergy}
                  setMinBessStoreEnergy={setMinBessStoreEnergy}
                  windTotalLossesPct={windTotalLossesPct}
                  setWindTotalLossesPct={setWindTotalLossesPct}
                  minDispatchPower={minDispatchPower}
                  setMinDispatchPower={setMinDispatchPower}
                  bessDegradation={bessDegradation}
                  setBessDegradation={setBessDegradation}
                  onBack={() => setCurrentStep(1)}
                  onSubmit={handleStep2Submit}
                  submitting={submitting}
                />
              )}

              {/* Step 3: Discharge Schedule Form */}
              {currentStep === 3 && (
                <StepDischargeSchedule
                  loadingDischarge={loadingDischarge}
                  savingDischarge={savingDischarge}
                  dischargeProfile={dischargeProfile}
                  setDischargeProfile={setDischargeProfile}
                  onBack={() => setCurrentStep(2)}
                  onSubmit={handleStep3DischargeSubmit}
                />
              )}

              {/* Step 4: Upload Profile Form */}
              {currentStep === 4 && (
                <StepUploadProfile
                  uploadFile={uploadFile}
                  setUploadFile={setUploadFile}
                  uploadError={uploadError}
                  setUploadError={setUploadError}
                  solarDegradation={solarDegradation}
                  setSolarDegradation={setSolarDegradation}
                  uploadDataType={uploadDataType}
                  setUploadDataType={setUploadDataType}
                  profileFilename={profileFilename}
                  loadedFromHistory={loadedFromHistory}
                  uploadingProfile={uploadingProfile}
                  handleDownloadTemplate={handleDownloadTemplate}
                  onBack={() => setCurrentStep(3)}
                  onSubmit={handleStep4UploadSubmit}
                />
              )}

              {/* Step 5: Run Simulation & In-page Results */}
              {currentStep === 5 && (
                <StepRunSimulation
                  runningSim={runningSim}
                  handleRunSimulation={handleRunSimulation}
                  loadedFromHistory={loadedFromHistory}
                  simulationResults={simulationResults}
                  selectedExportYear={selectedExportYear}
                  setSelectedExportYear={setSelectedExportYear}
                  handleDownloadYearExcel={handleDownloadYearExcel}
                  downloadingExcelYear={downloadingExcelYear}
                  handleLoadHourlyDetails={handleLoadHourlyDetails}
                  selectedDetailsYear={selectedDetailsYear}
                  selectedTrendFields={selectedTrendFields}
                  setSelectedTrendFields={setSelectedTrendFields}
                  selectedChartDay={selectedChartDay}
                  setSelectedChartDay={setSelectedChartDay}
                  loadingHourlyData={loadingHourlyData}
                  hourlyProfileData={hourlyProfileData}
                  onResetOrCreateNew={() => {
                    startFreshSimulation();
                  }}
                  onBack={() => setCurrentStep(4)}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <HybridHistoryTable
              loadingList={loadingList}
              hybridSimulations={hybridSimulations}
              bessSimulations={bessSimulations}
              onLoadSimulation={handleLoadSimulation}
              onViewResults={(simId) => navigate(`/hybrid-bess-wind/${simId}`)}
              onDeleteSimulation={handleDeleteHybridSimulation}
            />
          </div>
        )}
      </div>
    </div>
  );
}
